import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { ReactNode } from 'react';

import { CompletionReward, MissionStatus, MissionStatusProgressType } from '@flipster/apis/v1/rewardHub/types';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { useOverlay } from '@flipster/overlay';
import { Button, ButtonProps } from '@flipster/react/design-system/button';
import { Modal } from '@flipster/react/design-system/modal/index';
import { useBreakpoint } from '@flipster/react/misc/responsive/index';
import { css } from '@flipster/styled-system/css';
import { vstack } from '@flipster/styled-system/patterns';
import { getUserAgentInfo } from '@flipster/utils/user-agent';
import webviewHandler from '@flipster/utils/webview-handler';

import { useAuth } from '../../../shared/auth/state';
import { useGetUrlWithAnonymousId } from '../../../shared/hooks/useGetUrlWithAnonymousId';
import { FlipsterIcon } from '../../../shared/logo';
import { useDepositModal } from '../../../shared/wallet-modal/deposit';
import { useIsWebview } from '../../../shared/webview';
import { rewardHubV2Bunja } from '../state';
import { ActionButtonMissionType } from '../types';
import { getActionButtonMissionType, getMissionStatusFlags } from './utils';

interface BaseButtonProps extends ButtonProps<'button'> {
  missionStatus: Omit<MissionStatus, 'progress'>;
  progress: MissionStatusProgressType;
}

interface ActionButtonProps extends BaseButtonProps {
  missionType: ActionButtonMissionType;
  isSecondary?: boolean;
  isActive?: boolean;
}

interface ClaimButtonProps extends BaseButtonProps {
  sectionIndex: number;
  missionIndex: number;
  completionReward: CompletionReward;
}

interface MissionCtaButtonProps extends Omit<ActionButtonProps, 'missionType'>, ClaimButtonProps {}

export const MissionCtaButton = ({
  missionStatus,
  progress,
  sectionIndex,
  missionIndex,
  completionReward,
  isActive,
  ...buttonProps
}: MissionCtaButtonProps) => {
  const { isClaimable, isAssigned, isValid } = getMissionStatusFlags(missionStatus);
  if (!isAssigned || !isValid) return null;

  if (isClaimable) {
    return (
      <ClaimButton
        {...buttonProps}
        missionStatus={missionStatus}
        progress={progress}
        sectionIndex={sectionIndex}
        missionIndex={missionIndex}
        completionReward={completionReward}
        hierarchy='primary'
        color='neutral'
      />
    );
  }
  const missionType = getActionButtonMissionType(progress);
  if (!missionType) return null;

  return (
    <ActionButton
      {...buttonProps}
      missionStatus={missionStatus}
      progress={progress}
      missionType={missionType}
      isActive={isActive}
    />
  );
};

export const ClaimButton = ({
  missionStatus,
  sectionIndex,
  missionIndex,
  completionReward,
  ...buttonProps
}: ClaimButtonProps) => {
  const { claimRewardHubV2MutationAtom } = useBunja(rewardHubV2Bunja);
  const { mutate, isPending } = useAtomValue(claimRewardHubV2MutationAtom);

  const { isClaimable } = getMissionStatusFlags(missionStatus);
  if (!isClaimable) return null;

  const onClick = () => {
    mutate({ sectionIndex, missionIndex, completionReward });
  };

  return (
    <Button
      color='neutral'
      hierarchy='primary'
      label={<FormattedMessage id='Reward_hub.r250213.Claim' />}
      onClick={onClick}
      disabled={isPending}
      css={{ minW: 66 }}
      {...buttonProps}
    />
  );
};

export const ActionButton = ({ missionStatus, missionType, isActive, ...buttonProps }: ActionButtonProps) => {
  const router = useRouter();
  const isWebview = useIsWebview();
  const overlay = useOverlay();
  const { isMobile } = useBreakpoint();
  const { isCookieLoggedIn } = useAuth();
  const getUrlWithAnonymousId = useGetUrlWithAnonymousId();
  const openDepositModal = useDepositModal();

  const label: Record<ActionButtonProps['missionType'], ReactNode> = {
    KYCVerification: <FormattedMessage id='Common.r250812.Verify' />,
    DepositAmount: <FormattedMessage id='Common.r251107.Deposit' />,
    TradingVolume: <FormattedMessage id='Common.r250401.Trade' />,
  };

  const clickActions: Record<ActionButtonProps['missionType'], () => void> = {
    KYCVerification: () =>
      webviewHandler.postMessage({ action: 'link', params: { page: 'user/kyc' } }, () => router.push('/user/kyc')),
    DepositAmount: () => webviewHandler.postMessage({ action: 'link', params: { page: 'deposit' } }, openDepositModal),
    TradingVolume: () => {
      if (isMobile && !isWebview) {
        return overlay.open(({ isOpen, close, unmount }) => (
          <Modal.Root isOpen={isOpen} mode='bottomSheet' onClose={close} onExit={unmount}>
            <Modal.Content>
              <div className={vstack({ textAlign: 'center' })}>
                <FlipsterIcon type='rounded' color='blue' size='small' css={{ mb: 12 }} />
                <p className={css({ textStyle: 'Title/S', color: 'content.default.level-5', mb: 4 })}>
                  <FormattedMessage id='Reward_hub.r250213.Download_app_popup_title' />
                </p>
                <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
                  <FormattedMessage id='Reward_hub.r250213.Download_app_popup_description' />
                </p>
              </div>
            </Modal.Content>
            <Modal.Footer>
              <Modal.Button
                hierarchy='secondary'
                color='neutral'
                label={<FormattedMessage id='Common.r251008.Not_now' />}
                onClick={close}
              />
              <Modal.Button
                hierarchy='primary'
                label={<FormattedMessage id='Reward_hub.r250213.Download_app' />}
                onClick={() => {
                  const path = getUserAgentInfo().isAndroid
                    ? getUrlWithAnonymousId('PlaystoreDownload')
                    : getUrlWithAnonymousId('Download');
                  window.location.href = path;
                }}
              />
            </Modal.Footer>
          </Modal.Root>
        ));
      }
      return webviewHandler.postMessage({ action: 'link', params: { page: 'trade/latest' } }, () =>
        router.push('/trade'),
      );
    },
  };

  const onClick = () => {
    if (!isCookieLoggedIn && missionType !== 'TradingVolume') {
      webviewHandler.postMessage({ action: 'link', params: { page: 'login' } }, () => router.push('/login'));
      return;
    }
    clickActions[missionType]();
  };

  if (missionStatus.itemStatus === 'ASSIGNED' && isActive)
    return (
      <Button label={label[missionType]} onClick={onClick} css={{ minW: { base: 66, tablet: 328 } }} {...buttonProps} />
    );

  return null;
};
